import { K8sProvider } from '@cast/shared-types';
import {
  AgentStatus,
  Cluster,
  ClusterDisplayStatus,
  ClusterResponse,
  ClusterStatus,
} from '@cast/types';

import { isClusterManaged } from './utils/isClusterManaged';
import { isNotFoundCluster } from './utils/isNotFoundCluster';
import { isKentManagedCluster } from './utils/kent';

export const isDiscovered = (cluster: ClusterResponse): boolean => {
  const isManaged = isClusterManaged(cluster);
  const isAgentConnecting = cluster.agentStatus === AgentStatus.CONNECTING;
  const isSnapshotReceived = cluster.agentSnapshotReceivedAt;

  // discovered tag is still present after cluster gets connected
  // so if it has been connected it should have credentialsId or a snapshot date and it is already beyond discovered status
  return (
    !isManaged &&
    isAgentConnecting &&
    !isSnapshotReceived &&
    cluster.tags?.['cloud-discovery'] === 'true'
  );
};

export const transformStatus = (cluster: ClusterResponse): ClusterStatus => {
  const isKentCluster = isKentManagedCluster(cluster);

  if (isDiscovered(cluster)) {
    return ClusterStatus.DISCOVERED;
  }
  if (
    cluster.status === ClusterStatus.READY &&
    !cluster.credentialsId &&
    cluster.providerType !== K8sProvider.ANYWHERE &&
    !isKentCluster
  ) {
    return ClusterStatus.READ_ONLY;
  }
  return cluster.status;
};

const clusterStatusToDisplay: Record<ClusterStatus, ClusterDisplayStatus> = {
  [ClusterStatus.CONNECTING]: ClusterDisplayStatus.CONNECTING,
  [ClusterStatus.READ_ONLY]: ClusterDisplayStatus.READ_ONLY,
  [ClusterStatus.READY]: ClusterDisplayStatus.READY,
  [ClusterStatus.HIBERNATED]: ClusterDisplayStatus.HIBERNATED,
  [ClusterStatus.HIBERNATING]: ClusterDisplayStatus.HIBERNATING,
  [ClusterStatus.RESUMING]: ClusterDisplayStatus.RESUMING,
  [ClusterStatus.DELETED]: ClusterDisplayStatus.DELETED,
  [ClusterStatus.DELETING]: ClusterDisplayStatus.DELETING,
  [ClusterStatus.WARNING]: ClusterDisplayStatus.WARNING,
  [ClusterStatus.FAILED]: ClusterDisplayStatus.FAILED,
  [ClusterStatus.DISCOVERED]: ClusterDisplayStatus.DISCOVERED,
  [ClusterStatus.NOT_FOUND]: ClusterDisplayStatus.NOT_FOUND,
  [ClusterStatus.ARCHIVED]: ClusterDisplayStatus.ARCHIVED,
};

const agentStatusToDisplay: Record<AgentStatus, ClusterDisplayStatus> = {
  [AgentStatus.ONLINE]: ClusterDisplayStatus.READY,
  [AgentStatus.CONNECTING]: ClusterDisplayStatus.CONNECTING,
  [AgentStatus.NOT_RESPONDING]: ClusterDisplayStatus.WARNING,
  [AgentStatus.DISCONNECTING]: ClusterDisplayStatus.DISCONNECTING,
  [AgentStatus.DISCONNECTED]: ClusterDisplayStatus.DISCONNECTED,
};

export const computeDisplayStatus = (
  cluster: ClusterResponse
): ClusterDisplayStatus => {
  if (isDiscovered(cluster)) {
    return ClusterDisplayStatus.DISCOVERED;
  }
  const { agentStatus, status, credentialsId, providerType } = cluster;

  if (isNotFoundCluster(cluster)) {
    return ClusterDisplayStatus.NOT_FOUND;
  }

  // If the cluster is deleted but the agent is in connecting state
  // the cluster should be treated as disconnected
  if (
    status === ClusterStatus.DELETED &&
    agentStatus === AgentStatus.CONNECTING
  ) {
    return ClusterDisplayStatus.DISCONNECTED;
  }

  const isAgentConnected = agentStatus === AgentStatus.ONLINE;

  const shouldOverrideStatus =
    status &&
    agentStatus !== AgentStatus.DISCONNECTING &&
    [
      ClusterStatus.HIBERNATED,
      ClusterStatus.HIBERNATING,
      ClusterStatus.RESUMING,
      ClusterStatus.DELETING,
      ClusterStatus.FAILED,
      ClusterStatus.ARCHIVED,
    ].includes(status);
  const showClusterStatus = isAgentConnected || shouldOverrideStatus;

  const isKentCluster = isKentManagedCluster(cluster);

  if (
    showClusterStatus &&
    !credentialsId &&
    providerType !== K8sProvider.ANYWHERE &&
    !isKentCluster
  ) {
    return ClusterDisplayStatus.READ_ONLY;
  }

  return showClusterStatus
    ? clusterStatusToDisplay[status]
    : agentStatusToDisplay[agentStatus];
};

export const enhanceCluster = (cluster: ClusterResponse): Cluster => {
  return {
    ...cluster,
    status: transformStatus(cluster),
    displayStatus: computeDisplayStatus(cluster),
  };
};
